import React from 'react'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSaveDraft } from './useSaveDraft'
import { cn } from '@/lib/utils'

/** A white card with an icon, a title and a line under it; "Edit" jumps back to a step */
export const WizardCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle?: string
  onEdit?: () => void
  className?: string
  children: React.ReactNode
}> = ({ icon: Icon, title, subtitle, onEdit, className, children }) => (
  <section
    className={cn('rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6', className)}
  >
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" />
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground">{title}</h3>
          {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 text-sm font-medium text-[#2563EB] hover:underline"
        >
          Edit
        </button>
      ) : null}
    </div>
    <div className="space-y-5">{children}</div>
  </section>
)

/** A field label, with an optional "(optional)" and a red star when required */
export const FieldLabel: React.FC<{
  htmlFor?: string
  required?: boolean
  optional?: boolean
  children: React.ReactNode
}> = ({ htmlFor, required, optional, children }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-foreground">
    {children}
    {required ? <span className="ml-0.5 text-[#DC2626]">*</span> : null}
    {optional ? <span className="ml-1 font-normal text-muted-foreground">(optional)</span> : null}
  </label>
)

/** "124/300" under a text field */
export const Counter: React.FC<{ value: string; max: number; hint?: string }> = ({
  value,
  max,
  hint,
}) => (
  <div className="mt-1 flex justify-between gap-3 text-xs text-muted-foreground">
    <span>{hint}</span>
    <span className="tabular-nums">
      {value.length}/{max}
    </span>
  </div>
)

/** A pill that toggles: lime when on */
export const Chip: React.FC<{
  on: boolean
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}> = ({ on, onClick, disabled, children }) => (
  <button
    type="button"
    aria-pressed={on}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-40',
      on
        ? 'border-[#9BD85A] bg-[#E8F9D2] text-[#1F3D05]'
        : 'border-border bg-card text-foreground hover:border-[#9BD85A]',
    )}
  >
    {children}
  </button>
)

/** Back, Save as draft, and the main action, along the bottom of every step */
export const WizardFooter: React.FC<{
  onBack?: () => void
  primaryLabel: string
  primaryIcon?: React.ReactNode
  onPrimary?: () => void
  primaryDisabled?: boolean
  primaryBusy?: boolean
  submit?: boolean
}> = ({ onBack, primaryLabel, primaryIcon, onPrimary, primaryDisabled, primaryBusy, submit }) => {
  const { save, saving } = useSaveDraft()
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
      <div className="flex gap-2">
        {onBack ? (
          <Button type="button" variant="outline" onClick={onBack} className="bg-card">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          onClick={save}
          disabled={saving || primaryBusy}
          className="bg-card"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save as draft
        </Button>
      </div>
      <Button
        type={submit ? 'submit' : 'button'}
        variant="lime"
        onClick={onPrimary}
        disabled={primaryDisabled || primaryBusy || saving}
        className="min-w-[150px]"
      >
        {primaryBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {primaryLabel}
        {primaryIcon ?? (primaryBusy ? null : <ArrowRight className="h-4 w-4" />)}
      </Button>
    </div>
  )
}
